import { usePermissions, useOne, useUpdate } from '@refinedev/core';
import { ArrowLeft, Eye, Loader2, Pencil, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AccountPipeline } from '@/components/account-pipeline';
import { hasPermission } from '@/lib/auth-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { STATUS_LABEL, formatDate } from '@/lib/format';
import {
  ACCOUNT_STATUSES,
  type AccountStatus,
  type PsnAccountDetail,
} from '@/lib/types';
import { AuditTab } from './audit-tab';
import { EditCredentialsDialog } from './edit-credentials-dialog';
import { ImportConfirmDialog } from './import-confirm-dialog';
import { PurchaseModeTab } from './purchase-mode';
import { RecoveryCodesTab } from './recovery-codes-tab';
import { RevealDialog } from './reveal-dialog';

export function PsnAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountId = id ?? '';

  const { data, isLoading, refetch } = useOne<PsnAccountDetail>({
    resource: 'psn-accounts',
    id: accountId,
  });

  const { mutate: update, isLoading: isUpdating } = useUpdate();
  const { data: permissions } = usePermissions<string[]>();
  // Contrato Q-246 pede slug 'psn.import'; aceita também a convenção com
  // dois-pontos usada nos slugs existentes (psn:read etc.).
  const canImport =
    hasPermission(permissions, 'psn.import') || hasPermission(permissions, 'psn:import');

  const [revealOpen, setRevealOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Modo Compra é a primeira tab; ?modo=compra abre direto nela (Fila → Comprar).
  const [tab, setTab] = useState(searchParams.get('modo') === 'compra' ? 'compra' : 'info');
  const [editCredsOpen, setEditCredsOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState('');
  const [editingStatus, setEditingStatus] = useState<AccountStatus | ''>('');
  const [dirty, setDirty] = useState(false);

  // The API returns the account as `data` AND counts at top level. The data
  // provider only forwards `data`, so we need a custom shape — but useOne
  // unwraps `result.data`. Account row is `data.data` after unwrap... no:
  // dataProvider.getOne returns `{ data: res.data }`, so useOne gives us
  // `data.data === PsnAccount` directly. Recovery counts get lost.
  // For Fase 1.2, count comes from refetch via /recovery-codes endpoint.
  const account = data?.data as unknown as PsnAccountDetail['data'] | undefined;

  useEffect(() => {
    if (account) {
      setEditingNotes(account.notes ?? '');
      setEditingStatus(account.status);
      setDirty(false);
    }
  }, [account?.id, account?.notes, account?.status]);

  const onSave = () => {
    if (!account) return;
    const changes: Record<string, unknown> = {};
    if (editingNotes !== (account.notes ?? '')) changes.notes = editingNotes || null;
    if (editingStatus && editingStatus !== account.status) changes.status = editingStatus;
    if (Object.keys(changes).length === 0) {
      toast.info('Nada pra atualizar.');
      return;
    }
    update(
      { resource: 'psn-accounts', id: account.id, values: changes },
      {
        onSuccess: () => {
          toast.success('Atualizado');
          setDirty(false);
          refetch();
        },
        onError: () => toast.error('Falha ao atualizar'),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (!account) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/contas">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Link>
        </Button>
        <p className="text-muted-foreground">Conta não encontrada.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2">
          <Link to="/contas">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Link>
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight font-mono">{account.email}</h1>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{account.region}</Badge>
              <Badge variant="muted">
                {account.created_method === 'manual' ? 'Manual' : 'Automatizada'}
              </Badge>
            </div>
            <AccountPipeline status={account.status} />
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditCredsOpen(true)}>
                <Pencil className="h-4 w-4" />
                Editar credenciais
              </Button>
              <Button variant="outline" onClick={() => setRevealOpen(true)}>
                <Eye className="h-4 w-4" />
                Revelar credenciais
              </Button>
            </div>
            {account.status === 'game_purchased' && (
              <div className="flex flex-col items-end gap-1">
                <Button size="lg" disabled={!canImport} onClick={() => setImportOpen(true)}>
                  <Upload className="h-4 w-4" />
                  Importar pro estoque de produção
                </Button>
                {!canImport && (
                  <span className="text-xs text-muted-foreground">Só admin pode importar</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="compra">Modo Compra</TabsTrigger>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="codes">Recovery codes</TabsTrigger>
          <TabsTrigger value="audit">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="compra">
          {/* Tab inativa é desmontada pelo Radix — segredos saem do estado ao sair daqui. */}
          <PurchaseModeTab account={account} onPurchased={() => refetch()} />
        </TabsContent>

        <TabsContent value="info" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Identidade</CardTitle>
              <CardDescription>Campos read-only definidos na criação.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <ReadField label="PSN Online ID" value={account.psn_online_id} />
              <ReadField label="Data nasc." value={account.dob} />
              <ReadField label="Provedor SMS" value={account.phone_provider} />
              <ReadField label="Proxy" value={account.proxy_id} />
              <ReadField label="AdsPower profile" value={account.adspower_profile_id} />
              <ReadField label="Tenant" value={account.tenant_id} mono />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Estado</CardTitle>
              <CardDescription>Editar status, region (futuro) e notas.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editingStatus || account.status}
                    onValueChange={(v) => {
                      setEditingStatus(v as AccountStatus);
                      setDirty(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <ReadField label="Última ação" value={formatDate(account.last_action_at)} />
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  rows={4}
                  value={editingNotes}
                  onChange={(e) => {
                    setEditingNotes(e.target.value);
                    setDirty(true);
                  }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  disabled={!dirty || isUpdating}
                  onClick={() => {
                    setEditingNotes(account.notes ?? '');
                    setEditingStatus(account.status);
                    setDirty(false);
                  }}
                >
                  Reverter
                </Button>
                <Button onClick={onSave} disabled={!dirty || isUpdating}>
                  {isUpdating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Salvar alterações
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timestamps</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <ReadField label="Criada em" value={formatDate(account.created_at)} />
              <ReadField label="Atualizada em" value={formatDate(account.updated_at)} />
              <ReadField label="Primeira vez usada" value={formatDate(account.first_used_at)} />
              <ReadField label="Última ação" value={formatDate(account.last_action_at)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="codes">
          <RecoveryCodesTab accountId={account.id} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditTab accountId={account.id} />
        </TabsContent>
      </Tabs>

      <RevealDialog accountId={account.id} open={revealOpen} onOpenChange={setRevealOpen} />
      <ImportConfirmDialog
        accountId={account.id}
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => refetch()}
      />
      <EditCredentialsDialog
        accountId={account.id}
        accountEmail={account.email}
        open={editCredsOpen}
        onOpenChange={setEditCredsOpen}
        onSaved={() => refetch()}
      />
    </div>
  );
}

function ReadField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase text-muted-foreground">{label}</Label>
      <div className={mono ? 'font-mono text-sm' : 'text-sm'}>{value || '—'}</div>
    </div>
  );
}
